/**
 * Installing the app (#203, plan 06 §"Install prompt", ADR 0103).
 *
 * Chromium browsers fire `beforeinstallprompt` once the app is installable.
 * Left alone, a phone shows its own install bar on the first visit, before the
 * person has any idea whether they want this app on their home screen. So the
 * event is captured as early as possible (`captureInstallPrompt`, from
 * main.tsx), its default suppressed, and offered later and quietly:
 *
 * - a dismissible banner, never in the first sessions: after
 *   {@link INSTALL_MIN_SESSIONS} sessions or {@link INSTALL_MIN_AGE_MS} since
 *   the first one, and not again for {@link INSTALL_SNOOZE_MS} after "Not now";
 * - always from Settings → General ("Install the app").
 *
 * iOS has no such event and no API at all: Safari (and, since iOS 16.4, the
 * other browsers there) install from the Share sheet, so iPhone and iPad get
 * the two steps in words instead.
 *
 * Everything remembered lives in localStorage and every access is guarded:
 * without storage the banner simply never shows (Settings still works).
 */
import { desktopApp } from '@/utils/desktop';

export const INSTALL_MIN_SESSIONS = 3;
export const INSTALL_MIN_AGE_MS = 2 * 24 * 60 * 60 * 1000;
export const INSTALL_SNOOZE_MS = 60 * 24 * 60 * 60 * 1000;

export const INSTALL_SESSIONS_KEY = 'ols-mail.install.sessions';
export const INSTALL_FIRST_SEEN_KEY = 'ols-mail.install.first-seen';
export const INSTALL_DISMISSED_KEY = 'ols-mail.install.dismissed-at';
/** sessionStorage: this tab has already been counted. */
export const INSTALL_COUNTED_KEY = 'ols-mail.install.counted';

/** The Chromium event; not in lib.dom. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type InstallSupport =
  /** The browser handed us an install prompt to show. */
  | 'prompt'
  /** iPhone or iPad, not yet on the home screen: instructions. */
  | 'ios'
  /** Already running as the installed app. */
  | 'installed'
  /** The desktop wrapper (ADR 0051): it is an app already. */
  | 'desktop'
  /** No prompt (yet), or a browser that cannot install web apps. */
  | 'unsupported';

type Listener = () => void;

let deferred: BeforeInstallPromptEvent | null = null;
let installedNow = false;
let captured = false;
const listeners = new Set<Listener>();

function changed(): void {
  for (const listener of listeners) listener();
}

/** `useSyncExternalStore` plumbing: the prompt arriving, being used, the app installed. */
export function subscribeToInstall(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Call once, early (main.tsx): the event can fire before React has rendered. */
export function captureInstallPrompt(): void {
  if (captured || typeof window === 'undefined') return;
  captured = true;
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
  countSession(Date.now());
}

function onBeforeInstallPrompt(event: Event): void {
  event.preventDefault();
  deferred = event as BeforeInstallPromptEvent;
  changed();
}

function onAppInstalled(): void {
  deferred = null;
  installedNow = true;
  changed();
}

/** Running as the installed app (any platform). */
export function isInstalledPwa(): boolean {
  if (installedNow) return true;
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch {
    // No matchMedia (an old engine, a test): fall through.
  }
  return (navigator as { standalone?: boolean }).standalone === true;
}

/**
 * iPhone, iPod or iPad. iPadOS reports itself as a Mac ("MacIntel"); a Mac
 * with a touch screen does not exist, so touch points tell them apart.
 */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export function installSupport(): InstallSupport {
  if (desktopApp() !== null) return 'desktop';
  if (isInstalledPwa()) return 'installed';
  if (deferred !== null) return 'prompt';
  if (isIos()) return 'ios';
  return 'unsupported';
}

/**
 * Shows the browser's install dialog. The event is single-use, so it is
 * dropped whatever the answer; a browser that is asked again later fires a
 * fresh one. Resolves to the person's choice, or `unavailable`.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (event === null) return 'unavailable';
  deferred = null;
  changed();
  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice.outcome;
  } catch {
    return 'unavailable';
  }
}

// --- the banner's memory -----------------------------------------------------

function readNumber(key: string): number | undefined {
  try {
    const value = Number(window.localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the banner stays away, which is the quiet failure.
  }
}

/** Counts this tab once per browser session (one sessionStorage flag per tab). */
export function countSession(now: number): void {
  try {
    if (window.sessionStorage.getItem(INSTALL_COUNTED_KEY) === '1') return;
    window.sessionStorage.setItem(INSTALL_COUNTED_KEY, '1');
  } catch {
    return;
  }
  write(INSTALL_SESSIONS_KEY, String((readNumber(INSTALL_SESSIONS_KEY) ?? 0) + 1));
  if (readNumber(INSTALL_FIRST_SEEN_KEY) === undefined) write(INSTALL_FIRST_SEEN_KEY, String(now));
}

/** "Not now" on the banner. */
export function dismissInstallBanner(now: number = Date.now()): void {
  write(INSTALL_DISMISSED_KEY, String(now));
  changed();
}

/** Whether the banner may show now: experienced enough, not snoozed, something to offer. */
export function installBannerDue(now: number = Date.now()): boolean {
  const support = installSupport();
  if (support !== 'prompt' && support !== 'ios') return false;
  const sessions = readNumber(INSTALL_SESSIONS_KEY) ?? 0;
  const firstSeen = readNumber(INSTALL_FIRST_SEEN_KEY);
  if (firstSeen === undefined) return false;
  const familiar = sessions >= INSTALL_MIN_SESSIONS || now - firstSeen >= INSTALL_MIN_AGE_MS;
  if (!familiar) return false;
  const dismissed = readNumber(INSTALL_DISMISSED_KEY);
  return dismissed === undefined || now - dismissed >= INSTALL_SNOOZE_MS;
}

/** Test seam. */
export function resetInstallForTests(): void {
  window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.removeEventListener('appinstalled', onAppInstalled);
  deferred = null;
  installedNow = false;
  captured = false;
  listeners.clear();
}
